import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAuth } from '@src/context';
import { fetchForecast, fetchForecastWhatIf } from '@src/api/actions/dashboard/forecast';
import {
  ForecastSummaryHeader,
  SegmentCard,
} from './components/forecast/ForecastWidgets';

const WHATIF_DEBOUNCE_MS = 300;

export default function ForecastPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  const clientId = searchParams.get('client_id') || '';
  const [staffInput, setStaffInput] = useState(clientId);

  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [budgets, setBudgets] = useState({}); // budget_id -> monthly $
  const [whatif, setWhatif] = useState(null);
  const [whatifLoading, setWhatifLoading] = useState(false);
  const timerRef = useRef(null);

  // ---- load the baseline forecast ---------------------------------------
  useEffect(() => {
    if (!clientId) {
      setForecast(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setWhatif(null);
    fetchForecast({ client_id: clientId })
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data || resp;
        setForecast(data);
        const init = {};
        (data?.segments || []).forEach((s) => {
          init[s.budget_id] = s.base_budget;
        });
        setBudgets(init);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err?.response?.status === 403
            ? 'Forecast is not yet enabled for this account.'
            : 'Could not load the forecast for this client.'
        );
        setForecast(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // ---- debounced what-if recompute --------------------------------------
  const runWhatIf = useCallback(
    (nextBudgets) => {
      if (!forecast) return;
      const overrides = (forecast.segments || [])
        .filter((s) => Number(nextBudgets[s.budget_id]) !== Number(s.base_budget))
        .map((s) => ({ budget_id: s.budget_id, budget: nextBudgets[s.budget_id] }));
      if (overrides.length === 0) {
        setWhatif(null);
        return;
      }
      setWhatifLoading(true);
      fetchForecastWhatIf({ client_id: clientId, period: forecast.period, overrides })
        .then((resp) => setWhatif(resp?.data || resp))
        .catch(() => {})
        .finally(() => setWhatifLoading(false));
    },
    [forecast, clientId]
  );

  const handleSlider = useCallback(
    (budgetId, value) => {
      setBudgets((prev) => {
        const next = { ...prev, [budgetId]: value };
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => runWhatIf(next), WHATIF_DEBOUNCE_MS);
        return next;
      });
    },
    [runWhatIf]
  );

  const handleReset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const init = {};
    (forecast?.segments || []).forEach((s) => {
      init[s.budget_id] = s.base_budget;
    });
    setBudgets(init);
    setWhatif(null);
  }, [forecast]);

  // ---- derived ----------------------------------------------------------
  const displayScenarios = whatif?.scenarios || forecast?.scenarios;
  const outputsById = useMemo(() => {
    const src = whatif?.segments || forecast?.segments || [];
    const map = {};
    src.forEach((s) => {
      map[s.budget_id] = s.outputs;
    });
    return map;
  }, [whatif, forecast]);

  const recById = useMemo(() => {
    const map = {};
    (forecast?.recommendations || []).forEach((r) => {
      map[r.budget_id] = r;
    });
    return map;
  }, [forecast]);

  // Order segments by recommendation rank (exceptions first), then any leftovers.
  const orderedSegments = useMemo(() => {
    const segs = forecast?.segments || [];
    const byId = {};
    segs.forEach((s) => {
      byId[s.budget_id] = s;
    });
    const ordered = [];
    (forecast?.recommendations || []).forEach((r) => {
      if (byId[r.budget_id]) {
        ordered.push(byId[r.budget_id]);
        delete byId[r.budget_id];
      }
    });
    Object.values(byId).forEach((s) => ordered.push(s));
    return ordered;
  }, [forecast]);

  const daysInMonth = useMemo(() => {
    if (!forecast?.period) return 30;
    const [y, m] = forecast.period.split('-').map(Number);
    return new Date(y, m, 0).getDate();
  }, [forecast]);

  const periodLabel = forecast?.period
    ? new Date(`${forecast.period}T00:00:00`).toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      })
    : '';

  const dirty = useMemo(
    () => (forecast?.segments || []).some((s) => Number(budgets[s.budget_id]) !== Number(s.base_budget)),
    [budgets, forecast]
  );

  // ---- render -----------------------------------------------------------
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Forecast
          </Typography>
          {periodLabel && (
            <Typography variant="body2" color="text.secondary">
              Projecting {periodLabel} from recent performance
            </Typography>
          )}
        </Box>
        {dirty && (
          <Button size="small" onClick={handleReset} variant="outlined">
            Reset to current budgets
          </Button>
        )}
      </Stack>

      {isStaff && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
          <TextField
            size="small"
            label="Client ID"
            value={staffInput}
            onChange={(e) => setStaffInput(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && staffInput) setSearchParams({ client_id: staffInput });
            }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!staffInput}
            onClick={() => setSearchParams({ client_id: staffInput })}
          >
            Load
          </Button>
        </Stack>
      )}

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {!clientId && !error && (
        <Alert severity="info">Select a client to see their forecast.</Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && forecast && (
        <Stack spacing={3}>
          <ForecastSummaryHeader scenarios={displayScenarios} goalCom={forecast.goal_com} />

          <Divider />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                By segment
              </Typography>
              {whatifLoading && <CircularProgress size={16} />}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Each segment with our recommendation, its forecast, and a budget slider — drag to model
              a different month. Ranges widen as you scale past recent spend; actual results vary.
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              {orderedSegments.map((segment) => (
                <SegmentCard
                  key={segment.budget_id}
                  segment={segment}
                  rec={recById[segment.budget_id]}
                  value={budgets[segment.budget_id] ?? segment.base_budget}
                  outputs={outputsById[segment.budget_id]}
                  goalCom={forecast.goal_com}
                  daysInMonth={daysInMonth}
                  onChange={handleSlider}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
